import { CASINO_CATEGORY_IDS } from '../data-types/casino/casinoEnums';
import { SEO, SEO_SPORT_URL } from '../data-types/seo/seoEnums';
import { sportCategories } from '../data-types/sports/sportsConstants';
import { HORSE_RACE_LIST_ID, SPORT_LIST_ID } from '../data-types/sports/sportsEnums';
import { getBrandName } from '../international/internationalUtils';
import { CASINO_FILTER_NAME } from '../international/translation/casinoFilters';
import { getCasinoCategoryIdByPathName, getRawCasinoFilterName } from './casino';

const DESCRIPTION_SELECTOR = '#meta-description';
const TITLE_SELECTOR = '#title-title';
const CONTENT = 'content';
const DATA_IS_DEFAULT = 'data-is-default';
const ROBOTS_META_SELECTOR = `#meta-robots`;

const setMetaDescription = (newText: string) => {
  const description = window.document.querySelector(DESCRIPTION_SELECTOR);

  if (description) {
    description.setAttribute(CONTENT, newText);
    description.setAttribute(DATA_IS_DEFAULT, String(false));
  }
};

const setPageTitle = (newText: string) => {
  const title = window.document.querySelector(TITLE_SELECTOR);

  if (title) {
    title.innerHTML = newText;
    title.setAttribute(DATA_IS_DEFAULT, String(false));
  }
};

export const setNoIndexForRobots = () => {
  const tag = window.document.querySelector(ROBOTS_META_SELECTOR);

  if (tag) {
    tag.setAttribute(CONTENT, 'notranslate, noindex, nofollow');
  }
};

export const removeNoIndexForRobots = () => {
  const tag = window.document.querySelector(ROBOTS_META_SELECTOR);

  if (tag) {
    tag.setAttribute(CONTENT, 'notranslate, index, follow');
  }
};

const initializeMetaDescription = (newText: string) => {
  const description = window.document.querySelector(DESCRIPTION_SELECTOR);

  if (description && description.getAttribute(DATA_IS_DEFAULT) === String(true)) {
    description.setAttribute(CONTENT, newText);
    description.setAttribute(DATA_IS_DEFAULT, String(false));
  }
};

const initializePageTitle = (newText: string) => {
  const title = window.document.querySelector(TITLE_SELECTOR);

  if (title && title.getAttribute(DATA_IS_DEFAULT) === String(true)) {
    title.innerHTML = newText;
    title.setAttribute(DATA_IS_DEFAULT, String(false));
  }
};

export const setSEO = (section: string, formatMessage: Function) => {
  const domain = getBrandName();

  setMetaDescription(formatMessage({ id: `meta.description.${section}` }, { domain }));
  setPageTitle(formatMessage({ id: `meta.title.${section}` }, { domain }));
};

export const initializeSEO = (section: string, formatMessage: Function) => {
  const domain = getBrandName();

  initializeMetaDescription(formatMessage({ id: `meta.description.${section}` }, { domain }));
  initializePageTitle(formatMessage({ id: `meta.title.${section}` }, { domain }));
};

export const setSEOForCasino = (category: string, filter: string | undefined, formatMessage: Function) => {
  const categoryId = getCasinoCategoryIdByPathName(category);

  switch (categoryId) {
    case CASINO_CATEGORY_IDS.LIVE_CASINO: {
      removeNoIndexForRobots();
      const rawFilterName = getRawCasinoFilterName(filter);

      switch (rawFilterName) {
        case getRawCasinoFilterName(CASINO_FILTER_NAME.ROULETTE): {
          return setSEO('casino.live_casino.roulette', formatMessage);
        }
        case getRawCasinoFilterName(CASINO_FILTER_NAME.POKER): {
          return setSEO('casino.live_casino.poker', formatMessage);
        }
        case getRawCasinoFilterName(CASINO_FILTER_NAME.BLACKJACK): {
          return setSEO('casino.live_casino.blackjack', formatMessage);
        }
        case getRawCasinoFilterName(CASINO_FILTER_NAME.BACCARAT): {
          return setSEO('casino.live_casino.baccarat', formatMessage);
        }
        case getRawCasinoFilterName(CASINO_FILTER_NAME.DROPS_WINS): {
          return setSEO('casino.live_casino.drops_wins', formatMessage);
        }
        case getRawCasinoFilterName(CASINO_FILTER_NAME.NEW_AT_KWIFF_LIVE_CASINO):
        case getRawCasinoFilterName(CASINO_FILTER_NAME.NEW): {
          return setSEO('casino.live_casino.new', formatMessage);
        }
        case getRawCasinoFilterName(CASINO_FILTER_NAME.IT_IS_SHOW_TIME):
        case getRawCasinoFilterName(CASINO_FILTER_NAME.GAME_SHOWS): {
          return setSEO('casino.live_casino.game_shows', formatMessage);
        }
        case getRawCasinoFilterName(CASINO_FILTER_NAME.VIP_TABLES): {
          return setSEO('casino.live_casino.vip_tables', formatMessage);
        }
        default:
          return setSEO('casino.live_casino', formatMessage);
      }
    }

    case CASINO_CATEGORY_IDS.SLOTS: {
      setNoIndexForRobots();
      return setSEO('casino.slots', formatMessage);
    }

    case CASINO_CATEGORY_IDS.TABLE_GAMES: {
      removeNoIndexForRobots();
      return setSEO('casino.table_games', formatMessage);
    }

    case CASINO_CATEGORY_IDS.VIRTUAL_SPORTS: {
      removeNoIndexForRobots();
      return setSEO('casino.virtual_sports', formatMessage);
    }

    default: {
      removeNoIndexForRobots();
      return setSEO('casino.featured', formatMessage);
    }
  }
};

export const setSEOForSports = (sportId: string, sportSection: string | undefined, formatMessage: Function) => {
  if (sportId === SPORT_LIST_ID.IN_PLAY && !sportSection) {
    return setSEO('sports.live_now', formatMessage);
  }

  switch (Number(sportId)) {
    case sportCategories.FOOTBALL: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.football.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.football.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.football.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.football.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.HORSES: {
      switch (sportSection) {
        case HORSE_RACE_LIST_ID.NEXT_N:
          return setSEO('sports.horse_racing.next', formatMessage);
        case HORSE_RACE_LIST_ID.TODAY:
          return setSEO('sports.horse_racing.today', formatMessage);
        case HORSE_RACE_LIST_ID.TOMORROW:
          return setSEO('sports.horse_racing.tomorrow', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.TENNIS: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.tennis.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.tennis.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.tennis.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.tennis.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.BASKETBALL: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.basketball.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.basketball.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.basketball.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.basketball.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.GREY_HOUNDS: {
      switch (sportSection) {
        case SPORT_LIST_ID.NEXT:
          return setSEO('sports.greyhounds.next', formatMessage);
        case SPORT_LIST_ID.TODAY:
          return setSEO('sports.greyhounds.today', formatMessage);
        case SPORT_LIST_ID.TOMORROW:
          return setSEO('sports.greyhounds.tomorrow', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.GOLF: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.golf.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.golf.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.golf.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.golf.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.RUGBY: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.rugby.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.rugby.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.rugby.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.rugby.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.AMERICAN_FOOTBALL: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.american_football.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.american_football.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.american_football.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.american_football.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.DARTS: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.darts.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.darts.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.darts.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.darts.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.F1: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.f1.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.f1.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.f1.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.f1.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.SNOOKER: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.snooker.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.snooker.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.snooker.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.snooker.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.TABLE_TENNIS: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.table_tennis.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.table_tennis.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.table_tennis.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.table_tennis.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.BOXING: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.boxing.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.boxing.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.boxing.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.boxing.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.MMA: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.mma.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.mma.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.mma.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.mma.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.ICE_HOCKEY: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.ice_hockey.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.ice_hockey.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.ice_hockey.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.ice_hockey.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.BASEBALL: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.baseball.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.baseball.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.baseball.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.baseball.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.VOLLEYBALL: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.volleyball.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.volleyball.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.volleyball.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.volleyball.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.CRICKET: {
      switch (sportSection) {
        case SPORT_LIST_ID.IN_PLAY:
          return setSEO('sports.cricket.live_now', formatMessage);
        case SPORT_LIST_ID.TWENTY_FOUR_HRS.toLowerCase():
          return setSEO('sports.cricket.24hrs', formatMessage);
        case SPORT_LIST_ID.COMPETITIONS:
          return setSEO('sports.cricket.competitions', formatMessage);
        case SPORT_LIST_ID.CALENDAR:
          return setSEO('sports.cricket.calendar', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    case sportCategories.ELEAGUES: {
      switch (Number(sportSection)) {
        case sportCategories.E_FOOTBALL:
          return setSEO('sports.eleagues.efootball', formatMessage);
        case sportCategories.E_BASKETBALL:
          return setSEO('sports.eleagues.ebasketball', formatMessage);
        default:
          return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
      }
    }

    default: {
      return setSEO(SEO.SPORTS_DEFAULT, formatMessage);
    }
  }
};

export const getSEOSportUrl = (id: string | number) => {
  switch (Number(id)) {
    case sportCategories.FOOTBALL:
      return SEO_SPORT_URL.FOOTBALL;
    case sportCategories.TENNIS:
      return SEO_SPORT_URL.TENNIS;
    case sportCategories.HORSES:
      return SEO_SPORT_URL.HORSE_RACING;
    case sportCategories.CRICKET:
      return SEO_SPORT_URL.CRICKET;
    case sportCategories.RUGBY:
      return SEO_SPORT_URL.RUGBY;
    case sportCategories.DARTS:
      return SEO_SPORT_URL.DARTS;
    case sportCategories.SNOOKER:
      return SEO_SPORT_URL.SNOOKER;
    case sportCategories.BOXING:
      return SEO_SPORT_URL.BOXING;
    case sportCategories.MMA:
      return SEO_SPORT_URL.MMA;
    case sportCategories.BASKETBALL:
      return SEO_SPORT_URL.BASKETBALL;
    case sportCategories.ICE_HOCKEY:
      return SEO_SPORT_URL.ICE_HOCKEY;
    case sportCategories.AMERICAN_FOOTBALL:
      return SEO_SPORT_URL.AMERICAN_FOOTBALL;
    case sportCategories.GREY_HOUNDS:
      return SEO_SPORT_URL.GREYHOUNDS;
    case sportCategories.TABLE_TENNIS:
      return SEO_SPORT_URL.TABLE_TENNIS;
    case sportCategories.BASEBALL:
      return SEO_SPORT_URL.BASEBALL;
    case sportCategories.VOLLEYBALL:
      return SEO_SPORT_URL.VOLEYBALL;
    case sportCategories.GOLF:
      return SEO_SPORT_URL.GOLF;
    case sportCategories.F1:
      return SEO_SPORT_URL.F1;
    case sportCategories.ELEAGUES:
      return SEO_SPORT_URL.E_LEAGUES;
    case sportCategories.E_FOOTBALL:
      return SEO_SPORT_URL.E_FOOTBALL;
    case sportCategories.E_BASKETBALL:
      return SEO_SPORT_URL.E_BASKETBALL;
    default:
      return id;
  }
};

export const getSportIdBySportSEOUrl = (sport?: string) => {
  if (!sport) {
    return '';
  }

  switch (sport.toLowerCase()) {
    case SEO_SPORT_URL.FOOTBALL.toLowerCase():
      return sportCategories.FOOTBALL;
    case SEO_SPORT_URL.TENNIS.toLowerCase():
      return sportCategories.TENNIS;
    case SEO_SPORT_URL.HORSE_RACING.toLowerCase():
      return sportCategories.HORSES;
    case SEO_SPORT_URL.CRICKET.toLowerCase():
      return sportCategories.CRICKET;
    case SEO_SPORT_URL.RUGBY.toLowerCase():
      return sportCategories.RUGBY;
    case SEO_SPORT_URL.DARTS.toLowerCase():
      return sportCategories.DARTS;
    case SEO_SPORT_URL.SNOOKER.toLowerCase():
      return sportCategories.SNOOKER;
    case SEO_SPORT_URL.BOXING.toLowerCase():
      return sportCategories.BOXING;
    case SEO_SPORT_URL.MMA.toLowerCase():
      return sportCategories.MMA;
    case SEO_SPORT_URL.BASKETBALL.toLowerCase():
      return sportCategories.BASKETBALL;
    case SEO_SPORT_URL.ICE_HOCKEY.toLowerCase():
      return sportCategories.ICE_HOCKEY;
    case SEO_SPORT_URL.AMERICAN_FOOTBALL.toLowerCase():
      return sportCategories.AMERICAN_FOOTBALL;
    case SEO_SPORT_URL.GREYHOUNDS.toLowerCase():
      return sportCategories.GREY_HOUNDS;
    case SEO_SPORT_URL.TABLE_TENNIS.toLowerCase():
      return sportCategories.TABLE_TENNIS;
    case SEO_SPORT_URL.BASEBALL.toLowerCase():
      return sportCategories.BASEBALL;
    case SEO_SPORT_URL.VOLEYBALL.toLowerCase():
      return sportCategories.VOLLEYBALL;
    case SEO_SPORT_URL.GOLF.toLowerCase():
      return sportCategories.GOLF;
    case SEO_SPORT_URL.F1.toLowerCase():
      return sportCategories.F1;
    case SEO_SPORT_URL.E_LEAGUES.toLowerCase():
      return sportCategories.ELEAGUES;
    case SEO_SPORT_URL.E_FOOTBALL.toLowerCase():
      return sportCategories.E_FOOTBALL;
    case SEO_SPORT_URL.E_BASKETBALL.toLowerCase():
      return sportCategories.E_BASKETBALL;
    default:
      return sport;
  }
};
